/**
 * Branded type for date strings in YYYY-MM-DD format
 */
export type Day = string & { readonly __brand: 'Day' };

/**
 * Type guard to check if a string is a valid Day
 */
export const isDay = (value: string): value is Day => {
  const dayRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dayRegex.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  // Check if the date components match (this catches invalid dates like Feb 30)
  return date.getFullYear() === year &&
         date.getMonth() === month - 1 &&
         date.getDate() === day;
};

/**
 * Creates a Day from a Date object or timestamp
 */
export const toDay = (input: Date | number): Day => {
  const date = input instanceof Date ? input : new Date(input);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date input');
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dayString = `${year}-${month}-${day}`;

  return dayString as Day;
};

/**
 * Validates a string and returns it as a branded Day type
 * Returns null if the string is not in valid YYYY-MM-DD format
 */
export const asDay = (value: string): Day | null => {
  return isDay(value) ? value : null;
};

/**
 * Creates a Day from today's date
 */
export const today = (): Day => toDay(new Date());

/**
 * Parses a Day string to a Date object at midnight local time
 */
export const parseDay = (day: Day): Date => {
  const [year, month, dayNum] = day.split('-').map(Number);
  return new Date(year, month - 1, dayNum);
};